import { useMemo, useCallback } from 'react';
import Decimal from 'decimal.js';
import {
    parseDecimal,
    calculateMaxTrade,
    isBalanceSufficient,
    compareDecimals,
    MaxTradeResult,
} from '../utils/formatting';

/**
 * Hook for balance calculations with fee considerations
 */
export const useBalanceCalculator = (
    availableBalance: any,
    price?: any
) => {
    const balanceDec = useMemo(() => parseDecimal(availableBalance), [availableBalance]);
    const priceDec = useMemo(() => parseDecimal(price ?? 0), [price]);

    const maxBuy = useCallback(
        (fee: any = 0, includesFee: boolean = true): MaxTradeResult => {
            return calculateMaxTrade(balanceDec, priceDec, fee, includesFee);
        },
        [balanceDec, priceDec]
    );

    const isSufficient = useCallback(
        (amount: any, tolerance: number = 0.01): boolean => {
            return isBalanceSufficient(balanceDec, amount, tolerance);
        },
        [balanceDec]
    );

    return {
        balance: balanceDec,
        maxBuy,
        isSufficient,
    };
};

/**
 * Hook for comparing two decimal values with tolerance
 */
export const useDecimalComparison = (
    a: any,
    b: any,
    tolerance: number = 1e-8
) => {
    return useMemo(() => {
        const aDec = parseDecimal(a);
        const bDec = parseDecimal(b);
        const diff = aDec.minus(bDec).abs();
        const epsilon = new Decimal(tolerance);

        const isEqual = diff.lessThan(epsilon);
        const isGreater = aDec.greaterThan(bDec) && !isEqual;
        const isLess = aDec.lessThan(bDec) && !isEqual;

        return {
            isEqual,
            isGreater,
            isLess,
            difference: aDec.minus(bDec),
            comparisonResult: compareDecimals(a, b, tolerance),
        };
    }, [a, b, tolerance]);
};

/**
 * Hook for validating numeric inputs
 */
export const useNumericValidation = (
    maxValue?: any,
    maxDecimals: number = 8
) => {
    const maxDec = useMemo(
        () => (maxValue ? parseDecimal(maxValue) : null),
        [maxValue]
    );

    const isValid = useCallback(
        (value: any): boolean => {
            try {
                const dec = parseDecimal(value);

                if (maxDec && dec.greaterThan(maxDec)) {
                    return false;
                }

                const str = dec.toString();
                const parts = str.split('.');
                if (parts[1] && parts[1].length > maxDecimals) {
                    return false;
                }

                return true;
            } catch {
                return false;
            }
        },
        [maxDec, maxDecimals]
    );

    const sanitize = useCallback(
        (value: any): Decimal => {
            let dec = parseDecimal(value);

            if (maxDecimals) {
                const factor = new Decimal(10).pow(maxDecimals);
                dec = dec
                    .times(factor)
                    .toDecimalPlaces(0, Decimal.ROUND_DOWN)
                    .dividedBy(factor);
            }

            if (maxDec && dec.greaterThan(maxDec)) {
                dec = maxDec;
            }

            return dec;
        },
        [maxDec, maxDecimals]
    );

    return { isValid, sanitize };
};
